import useLeagueSelection from "../useLeagueSelection";
import { buildLeagueFilters } from "../leagueFilters";
import { HQ } from "../sportConfig";
import ShareButton from "../ShareButton";
import useMatchSort from "../useMatchSort";
import NotificationButton from "./NotificationButton";
import FootballBadge from "../sports/FootballBadge";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import "./MatchList.css";
import InstallApp from "./InstallApp";
import { useFavoriteTeams } from "../FavoriteTeams";
import { isProtectedMatch, usePushProtection } from '../notificationProtection';

const orderMatches = (matches, order) => {
  if (order !== 'time') {
    // Keep provider competition order, but alphabetize gender divisions within
    // the same competition (Dam/Damer before Herr/Herrar).
    const groups = new Map();
    const division = (name = '') => name.toLocaleLowerCase('sv').replace(/\b(?:damer|dam|herrar|herr)\b/g, '').replace(/\s+/g, ' ').trim();
    matches.forEach((match) => {
      const key = division(match.league);
      if (!groups.has(key)) groups.set(key, groups.size);
    });
    return [...matches].sort((left, right) =>
      groups.get(division(left.league)) - groups.get(division(right.league)) ||
      (left.league || '').localeCompare(right.league || '', 'sv'));
  }
  const minutes = (value) => {
    const parts = /^(\d{1,2}):(\d{2})$/.exec(value || '');
    if (!parts || Number(parts[1]) > 23 || Number(parts[2]) > 59) return Infinity;
    return Number(parts[1]) * 60 + Number(parts[2]);
  };
  // Stable sorting retains provider order for equal or unknown start times.
  return [...matches].sort((left, right) => minutes(left.time) - minutes(right.time));
};

const MatchList = ({ matches, cacheInfo, error }) => {
  usePushProtection();
  const navigate = useNavigate();
  const { teamIds, ready, authenticated, notice } = useFavoriteTeams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [hideResults, setHideResults] = useState(
    () => window.localStorage.getItem(`${HQ.storagePrefix}-hide-results`) === "true",
  );
  const [leagueMenuOpen, setLeagueMenuOpen] = useState(false);
  const [allLeagues, setAllLeagues] = useState([]);
  useEffect(() => {
    const controller = new AbortController();
    fetch(`${HQ.api}/sportsim/simulations`, { signal: controller.signal })
      .then((response) => { if (!response.ok) throw new Error(); return response.json(); })
      .then((rows) => setAllLeagues(rows))
      .catch((error) => { if (error.name !== "AbortError") setAllLeagues([]); });
    return () => controller.abort();
  }, []);

  // Hämta dagens datum i YYYY-MM-DD format
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const lastDailyDate = new Date(now);
  lastDailyDate.setDate(lastDailyDate.getDate() + 7);
  const lastDailyDateValue = `${lastDailyDate.getFullYear()}-${String(lastDailyDate.getMonth() + 1).padStart(2, "0")}-${String(lastDailyDate.getDate()).padStart(2, "0")}`;

  // Hämta valt datum från URL eller sätt till idag om inget anges
  const queryDate = searchParams.get("date");
  const selectedDate = queryDate ? queryDate : today;
  const selectedDateLabel = new Intl.DateTimeFormat("sv-SE", {
    weekday: "short",
    day: "numeric",
    month: "short",
    ...(selectedDate.slice(0, 4) !== today.slice(0, 4) ? { year: "numeric" } : {}),
  }).format(new Date(`${selectedDate}T12:00:00`)).replace(".", "");
  const [selectedLeagues, saveLeagues] = useLeagueSelection(searchParams, "daily");
  const mineOnly = searchParams.get("mine") === "1";
  const { savedOrder, saveOrder, ready: sortReady, notice: sortNotice } = useMatchSort();
  const requestedOrder = searchParams.get("sort");
  const sortOrder = requestedOrder === 'time' || requestedOrder === 'league' ? requestedOrder : savedOrder;
  const leagueFilters = useMemo(() => buildLeagueFilters(matches || [], allLeagues), [matches, allLeagues]);
  const leagues = leagueFilters.options;
  const firstOtherLeague = leagues.findIndex((league) => !league.daily);
  const filteredMatches = (matches || []).filter((match) =>
    leagueFilters.matchesSelection(match, selectedLeagues)
    && (!mineOnly || teamIds.includes(match.home_team_id) || teamIds.includes(match.away_team_id))
  );

  const updateParams = (updates) => {
    const nextParams = new URLSearchParams(searchParams);
    if (Object.prototype.hasOwnProperty.call(updates, "league")) saveLeagues(updates.league ? [updates.league] : []);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) nextParams.set(key, value);
      else nextParams.delete(key);
    });
    setSearchParams(nextParams);
  };

  const toggleLeague = (league) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("league");
    const nextLeagues = leagueFilters.selected(league, selectedLeagues)
      ? selectedLeagues.filter((item) => !leagueFilters.aliases.get(item)?.has(league.key))
      : [...selectedLeagues, league.name];
    saveLeagues(nextLeagues);
    nextLeagues.forEach((item) => nextParams.append("league", item));
    setSearchParams(nextParams);
  };

  const [pushNotice, setPushNotice] = useState('');
  const toggleHideResults = async () => {
    const nextValue = !hideResults;
    window.localStorage.setItem(`${HQ.storagePrefix}-hide-results`, String(nextValue));
    setHideResults(nextValue);
    try {
      if (HQ.capabilities.notifications) await window.SportsHQPush?.setHidden(HQ.sport, nextValue);
      setPushNotice('');
    } catch {
      setPushNotice('Resultat döljs här, men notisskyddet kunde inte sparas. Öppna Notiser och försök igen.');
    }
  };

  // Uppdatera URL vid datumändring
  const changeDate = (days) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + days);
    const formattedDate = newDate.toISOString().split("T")[0];
    updateParams({ date: formattedDate });
  };

  // Navigera till dagens datum
  const goToToday = () => {
    if (selectedDate !== today) {
      updateParams({ date: "" });
    }
  };

  if (error) {
    return <p className="error-message">⚠️ Det gick inte att hämta matcherna. Prova att ladda om sidan.</p>;
  }

  if (!matches) {
    return (
      <div className="loading-container">
        <div className="loader"></div>
        <p className="loading-text">Hämtar aktuella matcher, ett ögonblick...</p>
      </div>
    );
  }

  return (
    <div className="match-list-container">
      {/* Datum-navigering */}
      <div className="date-navigation">
        <button className="date-arrow" onClick={() => changeDate(-1)} aria-label="Föregående dag">‹</button>
        <span className="date-display">
          <span>{selectedDateLabel}</span>
          {selectedDate === today && <small>Idag</small>}
        </span>
        <button
          className="date-arrow"
          onClick={() => changeDate(1)}
          disabled={selectedDate >= lastDailyDateValue}
          aria-label="Nästa dag"
        >
          ›
        </button>
        {selectedDate !== today && (
          <button onClick={goToToday} className="today-button">Idag</button>
        )}
      </div>

      <div className="home-meta">{!window.SPORTS_HQ_PUSH_ENABLED && <ShareButton path={`/?date=${selectedDate}`} title={`${HQ.label} · ${selectedDateLabel}`} />}
        <Link className="tips-entry" to="/series">Serier <span aria-hidden="true">→</span></Link>
        <Link className="tips-entry" to="/tips">Tippa matcher <span aria-hidden="true">↗</span></Link>
        {window.SPORTS_HQ_PUSH_ENABLED && <div className="hq-home-notification-actions"><ShareButton path={`/?date=${selectedDate}`} title={`${HQ.label} · ${selectedDateLabel}`} /><NotificationButton day={selectedDate} /></div>}
        {matches.length > 0 && <select className="match-sort" aria-label="Sortera matcher" title={`Sortera efter ${sortOrder === 'time' ? 'starttid' : 'serie'}`}
          value={sortOrder} disabled={!sortReady} onChange={(event) => { saveOrder(event.target.value); updateParams({ sort: event.target.value }); }}>
          <option value="league">Serie</option>
          <option value="time">Starttid</option>
        </select>}
        {cacheInfo?.updated_at && (
          <p className="cache-status">
            Uppdaterad {new Date(cacheInfo.updated_at).toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" })}
            {cacheInfo.refreshing ? " · hämtar nytt i bakgrunden…" : ""}
          </p>
        )}
        <InstallApp />
      </div>

      {matches && (
        <div className="match-controls">
          {(leagues.length > 0 || selectedLeagues.length > 0) && <div className="match-filter match-filter--leagues">
              <span className="match-filter__label">Serier</span>
              <div className="league-dropdown">
                <button type="button" className="league-dropdown__trigger" aria-expanded={leagueMenuOpen}
                  aria-controls="home-league-options" onClick={() => setLeagueMenuOpen((open) => !open)}>
                  <span className="league-dropdown__summary-full">{selectedLeagues.length ? `${selectedLeagues.length} serier valda` : "Alla serier"}</span>
                  <span className="league-dropdown__summary-short">{selectedLeagues.length ? `${selectedLeagues.length} valda` : "Alla serier"}</span>
                  <span aria-hidden="true">▾</span>
                </button>
                {selectedLeagues.length > 0 && <button type="button" className="league-dropdown__clear"
                  onClick={() => updateParams({ league: "" })} aria-label="Rensa serieval">Rensa</button>}
                {leagueMenuOpen && <div id="home-league-options" className="league-options" role="group" aria-label="Filtrera på serier">
                  {leagues.map((league, index) => <React.Fragment key={league.key}>
                    {index === 0 && leagues.some((item) => item.daily) && <div className="league-options__heading">Matcher {selectedDate === today ? "idag" : "vald dag"}</div>}
                    {index === firstOtherLeague && <div className="league-options__heading league-options__heading--other">Övriga serier</div>}
                    <label className="league-option">
                    <input type="checkbox" checked={leagueFilters.selected(league, selectedLeagues)} onChange={() => toggleLeague(league)} />
                    <span>{league.name}</span>
                  </label></React.Fragment>)}
                </div>}
              </div>
              {selectedLeagues.length > 0 && <span className="match-filter__count">{filteredMatches.length} matcher</span>}
          </div>}
          <button type="button" className={`favorite-filter${mineOnly ? " favorite-filter--active" : ""}`}
            aria-pressed={mineOnly} disabled={!ready} onClick={() => updateParams({ mine: mineOnly ? "" : "1" })}>
            <span aria-hidden="true">★</span> Mina lag
          </button>
          <button
            type="button"
            className={`spoiler-toggle${hideResults ? " spoiler-toggle--active" : ""}`}
            aria-pressed={hideResults}
            onClick={toggleHideResults}
          >
            <span>Dölj resultat</span>
            <span className="spoiler-toggle__track" aria-hidden="true">
              <span className="spoiler-toggle__thumb" />
            </span>
          </button>
        </div>
      )}

      {notice && <p className="favorites-notice" role="status">{notice}</p>}
      {sortNotice && <p className="favorites-notice" role="status">{sortNotice}</p>}

      {matches.length === 0 && (
        <p className="no-matches">Inga matcher är registrerade för det här datumet.</p>
      )}

      {matches.length > 0 && filteredMatches.length === 0 && (
        <div className="no-filtered-matches">
          <p>{mineOnly && !teamIds.length ? "Du har inga favoritlag ännu. Öppna en match och tryck på stjärnan vid ett lag." : mineOnly ? "Dina lag har inga matcher här den här dagen." : "Inga matcher i den valda serien."}</p>
          {mineOnly && !authenticated && <small>Mina lag sparas på den här enheten.</small>}
          <button type="button" onClick={() => updateParams({ league: "", mine: "" })}>Visa alla matcher</button>
        </div>
      )}

      <div className="match-list match-list--compact">
        {pushNotice && <p role="alert">{pushNotice}</p>}
        {orderMatches(filteredMatches, sortOrder).map((match) => (
          <div
            key={match.id}
            className="match-card"
            onClick={() =>
              navigate(
                `/match?url=${encodeURIComponent(match.match_link + "/laguppstallning")}&returnTo=${encodeURIComponent(`/${searchParams.toString() ? `?${searchParams.toString()}` : ""}`)}`,
                { state: { match } },
              )
            }
          >
            <div className="league-info">
              {match.league_logo && (
                <img src={match.league_logo} alt={match.league} className="league-logo" />
              )}
              {match.league}
            </div>

            <div className="match-details">
              <div className="team">
                {HQ.sport === "football" ? <FootballBadge src={match.home_logo} name={match.home_team}/> : <img src={match.home_logo} alt={match.home_team} className="team-logo" />}
                <span className="team-name">{match.home_team}</span>
              </div>

              <div className="match-time">
                {(hideResults || isProtectedMatch(match)) && (match.status?.status_id > 1 || (HQ.sport === "football" && match.status?.home_goals != null)) ? (
                  <span className="hidden-result">Resultat dolt</span>
                ) : (match.status?.status_id > 1 || (HQ.sport === "football" && match.status?.home_goals != null)) && match.status?.home_goals !== null
                  ? `${match.status.home_goals} – ${match.status.away_goals}`
                  : match.time}
                {!hideResults && !isProtectedMatch(match) && (match.status?.status_id > 1 || (HQ.sport === "football" && match.status?.home_goals != null)) && (
                  <span className={`match-status match-status--${match.status.status_id}`}>
                    {match.status.label}
                  </span>
                )}
              </div>

              <div className="team">
                <span className="team-name">{match.away_team}</span>
                {HQ.sport === "football" ? <FootballBadge src={match.away_logo} name={match.away_team}/> : <img src={match.away_logo} alt={match.away_team} className="team-logo" />}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MatchList;
